import { EntityPagedResult } from "@bndes/bndes-ux4/base-entity";
import { inject, Injectable, Signal } from "@angular/core";
import { HttpClient, httpResource, HttpResourceRef } from "@angular/common/http";
import { map, Observable } from "rxjs";
import { UsuarioIewDTO } from "../model/usuario-iew-dto.entity";
import { PerfilAtribuidoDTO } from "../model/permissao-atribuida-iew-dto";
import { PerfilIewDTO } from "../model/perfil-iew-dto.model";
import { BnCpfCnpjUtils } from "@bndes/bndes-ux4/utils";

@Injectable({
  providedIn: "root",
})
export class GestaoUsuarioService {
  private readonly http = inject(HttpClient);
  BASE_URL = "./rest";

  public listarPerfisAtribuiveisOuDoUsuarioLogado(options?: {}): Observable<PerfilIewDTO[]> {
    return this.http.get<PerfilIewDTO[]>(`${this.BASE_URL}/gestao-usuario/listar-perfis-atribuiveis`);
  }

  public getPermissoesDoUsuario(idUsuario, options?: {}): Observable<PerfilAtribuidoDTO[]> {
    return this.http.get<PerfilAtribuidoDTO[]>(`${this.BASE_URL}/gestao-usuario/${idUsuario}/permissoes`, options);
  }

  public getPermissoesNaoGerenciaveisDoUsuario(idUsuario, options?: {}): Observable<PerfilIewDTO[]> {
    return this.http.get<PerfilIewDTO[]>(
      `${this.BASE_URL}/gestao-usuario/${idUsuario}/permissoes-nao-gerenciaveis`,
      options,
    );
  }

  public getPermissoesDisponiveis(): Observable<PerfilIewDTO[]> {
    return this.http.get<PerfilIewDTO[]>(`${this.BASE_URL}/gestao-usuario/permissoes-disponiveis`);
  }

  public getUsuarioLogadoIsECnpjMaster(options?: {}): Observable<boolean> {
    return this.http.get<boolean>(`${this.BASE_URL}/gestao-usuario/isECnpjMaster`, options);
  }

  public getUsuarioPeloId(idUsuario, options?: {}): Observable<UsuarioIewDTO> {
    return this.http.get<UsuarioIewDTO>(`${this.BASE_URL}/gestao-usuario/${idUsuario}`, options);
  }

  // Listagem de usuários do usuário bndes
  public getUsuariosDaEmpresa(
    cnpj: string,
    chavePesquisa: string,
    options?: {},
  ): Observable<EntityPagedResult<UsuarioIewDTO>> {
    const params = `cnpj=${cnpj}&chavePesquisa=${encodeURIComponent(chavePesquisa)}`;
    return this.getListaDeUsuarios(params, options);
  }

  public getUsuariosDaEmpresaInclusiveExcluidos(
    cnpj: string,
    chavePesquisa?: string,
    options?: {},
  ): Observable<EntityPagedResult<UsuarioIewDTO>> {
    let params = `cnpj=${cnpj}`;
    if (chavePesquisa) {
      params += `&chavePesquisa=${encodeURIComponent(chavePesquisa)}`;
    }
    params += `&exibirExcluidos=true`;
    return this.getListaDeUsuarios(params, options);
  }

  getQuantidadeDeUsuariosDaEmpresaHttpResource(
    cnpjSignal: Signal<string | undefined>,
  ): HttpResourceRef<number | undefined> {
    return httpResource<number | undefined> (
      () => {
        const cnpj = cnpjSignal();

        let params = `cnpj=${cnpj}`;

        return `${this.BASE_URL}/gestao-usuario/qtdUsuarios?${params}`;
      }
    );
  }

  getUsuariosDaEmpresaHttpResource(
    usuarioLogadoEhExternoSignal: Signal<boolean | undefined>,
    cnpjSignal: Signal<string | undefined>,
    exibirExcluidosSignal: Signal<boolean | undefined>
  ): HttpResourceRef<UsuarioIewDTO[] | undefined> {
    return httpResource<UsuarioIewDTO[] | undefined> (
      () => {
        const usuarioLogadoEhExterno = usuarioLogadoEhExternoSignal();
        const cnpj = cnpjSignal();
        const exibirExcluidos = exibirExcluidosSignal()??false;


        let params = usuarioLogadoEhExterno ? '' : `cnpj=${cnpj}&`;
        params += `exibirExcluidos=${exibirExcluidos}`;

        return `${this.BASE_URL}/gestao-usuario/listarUsuarios?${params}`;
      },{
        parse: (pagedResult: EntityPagedResult<UsuarioIewDTO>) => pagedResult.list
      }
    );
  }

  // Listagem de usuários do usuário externo
  public getUsuariosDaEmpresaDoUsuarioLogado(
    chavePesquisa: string,
    incluirExcluidos: boolean = false,
    options?: {},
  ): Observable<EntityPagedResult<UsuarioIewDTO>> {
    let params = `chavePesquisa=${encodeURIComponent(chavePesquisa)}`;
    if (incluirExcluidos) {
      params += `&exibirExcluidos=true`;
    }
    return this.getListaDeUsuarios(params, options);
  }

  public bloquearUsuario(idUsuario, options?: {}): Observable<boolean> {
    return this.http.put<boolean>(`${this.BASE_URL}/iew/usuario/${idUsuario}/bloquear`, options);
  }

  public desbloquearUsuario(idUsuario, options?: {}): Observable<boolean> {
    return this.http.put<boolean>(`${this.BASE_URL}/iew/usuario/${idUsuario}/desbloquear`, options);
  }

  public excluirUsuario(idUsuario, options?: {}): Observable<any> {
    return this.http.delete<any>(`${this.BASE_URL}/gestao-usuario/${idUsuario}`, options);
  }

  public redefinirSenhaDoUsuario(idUsuario, options?: {}): Observable<any> {
    return this.http.put<any>(`${this.BASE_URL}/iew/usuario/${idUsuario}/redefinir-senha`, options);
  }

  salvarUsuario(usuario: UsuarioIewDTO, options?: {}): Observable<number> {
    return this.http.put<number>(`${this.BASE_URL}/gestao-usuario`, usuario, options);
  }

  private getListaDeUsuarios(params: string, options?: {}): Observable<EntityPagedResult<UsuarioIewDTO>> {
    return this.http
      .get<EntityPagedResult<UsuarioIewDTO>>(`${this.BASE_URL}/gestao-usuario/listarUsuarios?${params}`, options)
      .pipe(
        map((pagedResult) => {
          const list = pagedResult.list.map((usuario) => {
            return new UsuarioIewDTO().extend(usuario);
          });
          return { ...pagedResult, list };
        }),
      );
  }

  buscaAvancadaDeUsuariosHttpResource(
    textoBuscadoSignal: Signal<string | undefined>,
    exibirExcluidosSignal: Signal<boolean | undefined>
  ): HttpResourceRef<UsuarioIewDTO[] | undefined> {
    return httpResource (
      () => {
        let chavePesquisa = textoBuscadoSignal();
        const exibirExcluidos = exibirExcluidosSignal()??false;
        if (!chavePesquisa || chavePesquisa?.length < 3) { return }
        if (BnCpfCnpjUtils.cpfValid(chavePesquisa)) {
          chavePesquisa = BnCpfCnpjUtils.removerFormatacao(chavePesquisa);
        }

        return `${this.BASE_URL}/gestao-usuario/buscaAvancadaDeUsuarios?chavePesquisa=${chavePesquisa}&exibirExcluidos=${exibirExcluidos}`;
      }
    );
  }

}


